/**
 * PreferredNamePrompt — Patch 24 Part V.
 *
 * A one-time modal shown after login when the authenticated user has
 * no ``preferred_name`` yet. Captures the conversational identity the
 * Marketing Department will use to address the owner going forward.
 *
 * Rules (per executive directive):
 *   • Never shown to guests.
 *   • Shown at most once — after successful save the field is set on
 *     the backend and the prompt does not appear again.
 *   • Dismissal without saving marks a local "skip" so the user isn't
 *     re-nagged on the same session; the next login will prompt again
 *     until ``preferred_name`` is populated server-side.
 *   • Never asks for legal name, never uses email as a fallback.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { useAuthStore } from "@/entities/auth/store";
import { useWorkspaceStore } from "@/app/store/workspace-store";
import { http } from "@/shared/api/http";


const SKIP_SESSION_KEY = "cz.preferred-name-prompt.skipped";

/** Surfaces that MUST be interactable without a modal overlay in the
 *  way. In particular, ``/workspaces/new`` is the mandatory first step
 *  for brand-new (Google-OAuth / no-workspace) users — showing the
 *  preferred-name prompt on top of that form left the visitor unable
 *  to reach the "Business name" input. (Patch 37.4.13 follow-up.) */
const SUPPRESSED_PATHS = new Set<string>([
  "/workspaces/new",
  "/onboarding",
  "/genesis",
  "/welcome",
  "/login",
  "/auth/callback",
]);


export function PreferredNamePrompt(): JSX.Element | null {
  const qc = useQueryClient();
  const isGuest = useAuthStore((s) => s.guest.active && !s.accessToken);
  const authUser = useAuthStore((s) => s.user);
  const me = useWorkspaceStore((s) => s.me);
  const hydrated = useWorkspaceStore((s) => s.hydrated);
  const workspacesLength = useWorkspaceStore((s) => s.workspaces.length);
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  // Consult both stores — auth-store.user carries preferred_name after
  // login; workspace-store.me carries it after the /me hydration.
  const preferred =
    (me?.preferred_name ?? "").trim() ||
    (authUser?.preferred_name ?? "").trim();

  useEffect(() => {
    if (isGuest) return setVisible(false);
    if (!hydrated) return;
    if (preferred) return setVisible(false);
    // Never cover the mandatory first-workspace form or other
    // provisioning surfaces (matches useEnsureWorkspaceForAuthedUser).
    if (SUPPRESSED_PATHS.has(location.pathname)) return setVisible(false);
    // Defer until the user has at least one workspace — for a fresh
    // Google-Auth account this means the prompt appears only AFTER
    // they've created their first workspace.
    if (workspacesLength === 0) return setVisible(false);
    // Do not prompt within a single session after a manual dismissal.
    if (sessionStorage.getItem(SKIP_SESSION_KEY)) return;
    setVisible(true);
  }, [isGuest, hydrated, preferred, location.pathname, workspacesLength]);

  const suggested =
    (me?.display_name ?? "").split(/\s+/)[0] ||
    (authUser?.name ?? "").split(/\s+/)[0] ||
    "";

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const updated = await http<{
        id: string; email: string; display_name: string; preferred_name: string; is_dev_user: boolean;
      }>("/me", {
        method: "PATCH",
        body: JSON.stringify({ preferred_name: trimmed }),
      });
      // Update both stores so every consumer picks up the new name
      // without a full refresh.
      useWorkspaceStore.getState().setMe({
        ...(me ?? {}),
        id: updated.id,
        email: updated.email,
        display_name: updated.display_name,
        preferred_name: updated.preferred_name,
        is_dev_user: updated.is_dev_user,
      });
      if (authUser) {
        useAuthStore.setState({
          user: { ...authUser, preferred_name: updated.preferred_name },
        });
      }
      void qc.invalidateQueries({ queryKey: ["me"] });
      toast.success(`We'll call you ${updated.preferred_name} from now on.`);
      setVisible(false);
    } catch {
      toast.error("We couldn't save that name. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const skip = () => {
    sessionStorage.setItem(SKIP_SESSION_KEY, "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="preferred-name-prompt"
        data-testid="preferred-name-prompt"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-40 grid place-items-center bg-[var(--md-ink-0)]/70 backdrop-blur-md px-4"
      >
        <motion.div
          initial={{ y: 12, scale: 0.98, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 8, opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.2, 0.7, 0.3, 1] }}
          className="md-panel--glass relative w-full max-w-lg overflow-hidden rounded-2xl md-glow shadow-[var(--shadow-lg)]"
        >
          <button
            onClick={skip}
            data-testid="preferred-name-skip"
            aria-label="Not right now"
            className="absolute right-4 top-4 z-10 rounded-full p-2 text-[var(--md-fog)] transition hover:bg-white/6 hover:text-[var(--md-ivory-0)]"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="grid gap-5 p-8">
            <div className="md-eyebrow md-eyebrow--amber inline-flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> Meet your Marketing Department
            </div>
            <h2 className="md-h1 text-3xl">
              What should we call you?
            </h2>
            <p className="text-sm text-[var(--md-fog)]">
              Your Marketing Department will use this name in your morning brief,
              approval nudges, and every conversation. It&apos;s not your legal name —
              just what you&apos;d like to be called.
            </p>
            <label className="block">
              <span className="md-eyebrow">Preferred name</span>
              <input
                data-testid="preferred-name-input"
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value.slice(0, 60))}
                placeholder={suggested || "e.g. Sarah, Coach Mike, or Freon"}
                className="mt-1 w-full rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-2)]/60 px-3 py-2 text-sm text-[var(--md-ivory-0)] placeholder:text-[var(--md-smoke)] focus:border-[var(--md-amber)] focus:outline-none"
                onKeyDown={(e) => { if (e.key === "Enter") void save(); }}
              />
            </label>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={skip}
                data-testid="preferred-name-later"
                className="md-btn md-btn--ghost"
              >
                Not right now
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving || !value.trim()}
                data-testid="preferred-name-save"
                className="md-btn md-btn--primary disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
